import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { 
  GraduationCap, 
  UserCheck, 
  Trophy, 
  ArrowRight, 
  CheckCircle,
  BookOpen,
  Users,
  Target
} from 'lucide-react';

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3rem 2rem;
  text-align: center;
  color: white;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 300;
`;

const Content = styled.div`
  padding: 3rem 2rem;
`;

const UserTypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const UserTypeCard = styled.div<{ isSelected: boolean; isActive: boolean }>`
  background: ${props => props.isSelected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f8fafc'};
  border: 2px solid ${props => props.isSelected ? '#667eea' : '#e2e8f0'};
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.isActive ? '#667eea' : '#cbd5e1'};
  }
  
  ${props => props.isSelected && `
    color: white;
    transform: translateY(-4px);
    box-shadow: 0 25px 50px -12px rgba(102, 126, 234, 0.25);
  `}
`;

const UserTypeIcon = styled.div<{ isSelected: boolean }>`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.2)' : '#667eea'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: ${props => props.isSelected ? 'white' : 'white'};
`;

const UserTypeTitle = styled.h3<{ isSelected: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${props => props.isSelected ? 'white' : '#1e293b'};
`;

const UserTypeDescription = styled.p<{ isSelected: boolean }>`
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  color: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.9)' : '#64748b'};
`;

const UserTypeFeatures = styled.ul<{ isSelected: boolean }>`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0 0 0;
  text-align: left;
`;

const UserTypeFeature = styled.li<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.9)' : '#64748b'};
  
  svg {
    margin-right: 0.5rem;
    color: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.8)' : '#667eea'};
  }
`;

const SelectionIndicator = styled.div<{ isSelected: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.isSelected ? 'white' : 'transparent'};
  border: 2px solid ${props => props.isSelected ? 'white' : '#e2e8f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.isSelected && `
    svg {
      color: #667eea;
    }
  `}
`;

const ActionSection = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border: 2px solid rgba(102, 126, 234, 0.1);
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  scroll-margin-top: 2rem;
  scroll-margin-bottom: 2rem;
  position: relative;
  z-index: 10;
  
  &:hover {
    border-color: rgba(102, 126, 234, 0.2);
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
  }
  
  /* Enhanced focus and scroll-to styles */
  &:focus-within {
    border-color: rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  /* Animation for when scrolled to */
  &.scrolled-to {
    animation: highlightSection 2s ease-in-out;
  }
  
  @keyframes highlightSection {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 20px rgba(102, 126, 234, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
    }
  }
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? '#cbd5e1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  border: 2px solid ${props => props.disabled ? '#a0aec0' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 0.75rem;
  padding: 1rem 3rem;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.025em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-width: 200px;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    background: ${props => props.disabled ? '#cbd5e1' : 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'};
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
    }
  }
`;

const InfoSection = styled.div`
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 2rem;
  margin-top: 2rem;
  text-align: center;
`;

const InfoTitle = styled.h4`
  color: #1e293b;
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
`;

const InfoText = styled.p`
  color: #64748b;
  margin: 0;
  line-height: 1.6;
`;

const BackToLogin = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const BackLink = styled.a`
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

// User type definitions
const userTypes = [
  {
    id: 'student',
    title: 'Student',
    description: 'Join our learning platform to access quality education, track your progress, and achieve your academic goals.',
    icon: <GraduationCap size={40} />,
    features: [
      'Access to comprehensive curriculum',
      'Progress tracking and analytics',
      'Interactive learning modules',
      'Performance assessments',
      'Personalized learning paths'
    ],
    route: '/auth/enhanced-student-register',
    color: '#667eea'
  },
  {
    id: 'teacher',
    title: 'Teacher',
    description: 'Empower students with your expertise. Create content, manage classes, and track student progress.',
    icon: <UserCheck size={40} />,
    features: [
      'Content creation tools',
      'Class management',
      'Student progress monitoring',
      'Assessment creation',
      'Professional development resources'
    ],
    route: '/auth/teacher-registration',
    color: '#764ba2'
  },
  {
    id: 'competitor',
    title: 'Competitor',
    description: 'Challenge yourself and earn rewards through competitive learning. Test your knowledge against peers.',
    icon: <Trophy size={40} />,
    features: [
      'Competitive challenges',
      'Real-time leaderboards',
      'Reward system',
      'Skill-based matchmaking',
      'Performance analytics'
    ],
    route: '/auth/compete-register',
    color: '#f59e0b'
  }
];

const UnifiedRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [selectedUserType, setSelectedUserType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const actionSectionRef = useRef<HTMLDivElement>(null);

  // Debug effect to log ref changes
  useEffect(() => {
    console.log('ActionSection ref updated:', actionSectionRef.current);
  }, [actionSectionRef.current]);

  // Enhanced scrolling function with fallbacks
  const scrollToActionSection = () => {
    if (!actionSectionRef.current) {
      console.warn('Action section ref not found');
      return;
    }
    
    const element = actionSectionRef.current;
    
    // Get the element's position relative to the viewport
    const elementRect = element.getBoundingClientRect();
    
    // Calculate the target scroll position to center the element
    const targetScrollTop = window.pageYOffset + elementRect.top - (window.innerHeight / 2) + (elementRect.height / 2);
    
    // Ensure we don't scroll beyond the document bounds
    const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    
    console.log('Scrolling details:', {
      elementRect,
      targetScrollTop,
      finalScrollTop,
      currentScrollTop: window.pageYOffset,
      windowHeight: window.innerHeight
    });
    
    // Try smooth scrolling first
    try {
      window.scrollTo({
        top: finalScrollTop,
        behavior: 'smooth'
      });
    } catch (error) {
      console.warn('Smooth scrolling failed, using fallback:', error);
      // Fallback for browsers that don't support smooth scrolling
      window.scrollTo(0, finalScrollTop);
    }
    
    // Add visual feedback with enhanced animation
    element.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    element.style.transform = 'scale(1.03)';
    element.style.boxShadow = '0 25px 50px -12px rgba(102, 126, 234, 0.35)';
    element.style.borderColor = 'rgba(102, 126, 234, 0.3)';
    
    // Add CSS class for enhanced animation
    element.classList.add('scrolled-to');
    
    // Reset the highlight effect
    setTimeout(() => {
      if (element) {
        element.style.transform = 'scale(1)';
        element.style.boxShadow = 'none';
        element.style.borderColor = 'rgba(102, 126, 234, 0.1)';
        element.classList.remove('scrolled-to');
      }
    }, 400);
  };

  // Fallback scrolling function using element.scrollIntoView
  const scrollToActionSectionFallback = () => {
    if (!actionSectionRef.current) return;
    
    try {
      actionSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    } catch (error) {
      console.warn('scrollIntoView failed, using basic scroll:', error);
      // Last resort: basic scroll
      if (actionSectionRef.current) {
        actionSectionRef.current.scrollIntoView();
      }
    }
  };

  // Intersection Observer based scrolling for better reliability
  const scrollToActionSectionWithObserver = () => {
    if (!actionSectionRef.current) return;
    
    const element = actionSectionRef.current;
    
    // Create an intersection observer to ensure the element is visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log('Element is now visible');
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.5, // Element is considered visible when 50% is in view
        rootMargin: '0px 0px -20% 0px' // Consider element visible when it's 20% from the bottom
      }
    );
    
    observer.observe(element);
    
    // Scroll to the element
    try {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    } catch (error) {
      console.warn('scrollIntoView with observer failed:', error);
      // Fallback to basic scroll
      element.scrollIntoView();
    }
    
    // Clean up observer after a delay
    setTimeout(() => {
      observer.disconnect();
    }, 2000);
  };

  const handleUserTypeSelect = (userTypeId: string) => {
    setSelectedUserType(userTypeId);
    
    // Auto-scroll to the continue button after a delay to ensure state update and DOM re-render is complete
    setTimeout(() => {
      console.log('Scrolling to action section...', { userTypeId, actionSectionRef: actionSectionRef.current });
      
      // Try the enhanced scrolling first
      scrollToActionSection();
      
      // If the first method doesn't work well, try the fallback after a short delay
      setTimeout(() => {
        scrollToActionSectionFallback();
      }, 100);
      
      // Try the observer-based method as a final fallback
      setTimeout(() => {
        scrollToActionSectionWithObserver();
      }, 200);
      
    }, 400);
  };

  const handleContinue = async () => {
    if (!selectedUserType) return;
    
    setIsLoading(true);
    
    try {
      const userType = userTypes.find(ut => ut.id === selectedUserType);
      if (userType) {
        // Add a small delay for better UX
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate(userType.route);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>Join Lavern Titan</HeaderTitle>
        <HeaderSubtitle>
          Choose your path by clicking on one of the 3 roles below.
        </HeaderSubtitle>
      </Header>
      
      <Content>
        <UserTypeSelector>
          {userTypes.map((userType) => (
            <UserTypeCard
              key={userType.id}
              isSelected={selectedUserType === userType.id}
              isActive={true}
              onClick={() => handleUserTypeSelect(userType.id)}
            >
              <SelectionIndicator isSelected={selectedUserType === userType.id}>
                {selectedUserType === userType.id && <CheckCircle size={16} />}
              </SelectionIndicator>
              
              <UserTypeIcon isSelected={selectedUserType === userType.id}>
                {userType.icon}
              </UserTypeIcon>
              
              <UserTypeTitle isSelected={selectedUserType === userType.id}>
                {userType.title}
              </UserTypeTitle>
              
              <UserTypeDescription isSelected={selectedUserType === userType.id}>
                {userType.description}
              </UserTypeDescription>
              
              <UserTypeFeatures isSelected={selectedUserType === userType.id}>
                {userType.features.map((feature, index) => (
                  <UserTypeFeature key={index} isSelected={selectedUserType === userType.id}>
                    <CheckCircle size={16} />
                    {feature}
                  </UserTypeFeature>
                ))}
              </UserTypeFeatures>
            </UserTypeCard>
          ))}
        </UserTypeSelector>

        <ActionSection ref={actionSectionRef}>
          <ContinueButton
            disabled={!selectedUserType || isLoading}
            onClick={handleContinue}
            style={{
              animation: selectedUserType ? 'pulse 2s infinite' : 'none'
            }}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Preparing...
              </>
            ) : (
              <>
                Continue to {selectedUserType ? userTypes.find(ut => ut.id === selectedUserType)?.title : 'Registration'}
                <ArrowRight size={20} />
              </>
            )}
          </ContinueButton>
          
          {/* Debug button for testing scrolling */}
          <button
            onClick={() => {
              console.log('Manual scroll test');
              scrollToActionSection();
            }}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Test Scroll (Debug)
          </button>
        </ActionSection>

        <InfoSection>
          <InfoTitle>Why Choose Lavern Titan?</InfoTitle>
          <InfoText>
            Our platform provides a comprehensive learning ecosystem designed to support students, 
            empower teachers, and create engaging competitive experiences. Join thousands of users 
            who are already transforming their learning journey.
          </InfoText>
        </InfoSection>

        <BackToLogin>
          <BackLink onClick={handleBackToLogin}>
            ← Back to Sign In
          </BackLink>
        </BackToLogin>
      </Content>
    </Container>
  );
};

export default UnifiedRegistration;
